// Hooks de códigos de descuento y cupones: lista filtrable, crear y anular.
import {
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQueryClient,
    type InfiniteData,
    type QueryClient,
} from '@tanstack/react-query';
import { useState } from 'react';
import { shouldRetryPromotionsQuery } from '../lib/promotionsFormat';
import { isPromotionsConfigured, promotionsClient } from './promotions.client';
import type {
    CreateDiscountCodeRequest,
    DiscountCode,
    ListDiscountCodesQuery,
    ListDiscountCodesResponse,
} from './promotionsContract';
import { PROMOTIONS_QUERY_KEY } from './usePromotions';

const KEY = [...PROMOTIONS_QUERY_KEY, 'discount-codes'] as const;
const PAGE_SIZE = 50;

export type DiscountCodeFilters = Pick<ListDiscountCodesQuery, 'origin' | 'status'>;

/** Un código con la cadena de la que se leyó; en la lista de una sola cadena no la trae. */
export type ChainDiscountCode = DiscountCode & { retailerId?: string };

export interface DiscountCodesState {
    items: ChainDiscountCode[];
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isSuccess: boolean;
    error: unknown;
    fetchNextPage: () => Promise<unknown>;
    refetch: () => Promise<unknown>;
    /** Falla de «Cargar más» en alguna cadena; la lista ya cargada sigue en pantalla. */
    loadMoreError?: unknown;
}

/** Paginado con `nextToken`; `enabled: false` no pide nada. */
export function useDiscountCodes({
    filters,
    enabled,
}: {
    filters: DiscountCodeFilters;
    enabled: boolean;
}): DiscountCodesState {
    const query = useInfiniteQuery({
        queryKey: [...KEY, 'list', filters],
        queryFn: ({ pageParam }) =>
            promotionsClient.listDiscountCodes({
                origin: filters.origin,
                status: filters.status,
                limit: PAGE_SIZE,
                nextToken: pageParam,
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken ?? undefined,
        enabled: enabled && isPromotionsConfigured(),
        retry: shouldRetryPromotionsQuery,
        staleTime: 30_000,
    });
    return {
        items: query.data?.pages.flatMap((page) => page.items) ?? [],
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isSuccess: query.isSuccess,
        error: query.error,
        fetchNextPage: () => query.fetchNextPage(),
        refetch: () => query.refetch(),
    };
}

type CodePages = InfiniteData<ListDiscountCodesResponse, string | undefined>;

const codesKey = (filters: DiscountCodeFilters, retailerId: string) =>
    [...KEY, 'list', filters, retailerId] as const;

function fetchCodePage(filters: DiscountCodeFilters, retailerId: string, nextToken?: string) {
    return promotionsClient.listDiscountCodes({
        origin: filters.origin,
        status: filters.status,
        limit: PAGE_SIZE,
        nextToken,
        retailerId,
    });
}

/** Al refrescar se vuelven a leer tantas páginas como ya había, para no perder lo cargado. */
async function fetchLoadedPages(
    qc: QueryClient,
    filters: DiscountCodeFilters,
    retailerId: string,
): Promise<CodePages> {
    const loaded = qc.getQueryData<CodePages>(codesKey(filters, retailerId))?.pages.length ?? 1;
    const data: CodePages = { pages: [], pageParams: [] };
    let token: string | undefined;
    do {
        const page = await fetchCodePage(filters, retailerId, token);
        data.pages.push(page);
        data.pageParams.push(token);
        token = page.nextToken ?? undefined;
    } while (token !== undefined && data.pages.length < loaded);
    return data;
}

/**
 * Los códigos de varias cadenas juntos, cada cadena con su lectura y su caché; cada código lleva
 * la cadena de la que salió porque el back no la incluye.
 */
export function useDiscountCodesByRetailer({
    filters,
    retailerIds,
    enabled,
    retailersLoading = false,
}: {
    filters: DiscountCodeFilters;
    retailerIds: readonly string[];
    enabled: boolean;
    /** Mientras llegan las cadenas la lista no se da por vacía. */
    retailersLoading?: boolean;
}): DiscountCodesState {
    const qc = useQueryClient();
    const [fetchingMore, setFetchingMore] = useState(false);
    // La falla de «Cargar más» es de los filtros con que se pidió: con otros ya no aplica.
    const filtersKey = JSON.stringify(filters);
    const [loadMoreFailure, setLoadMoreFailure] = useState<{ key: string; error: unknown } | null>(
        null,
    );
    const loadMoreError = loadMoreFailure?.key === filtersKey ? loadMoreFailure.error : null;
    const fetchPage = (retailerId: string, nextToken?: string) =>
        fetchCodePage(filters, retailerId, nextToken);
    const results = useQueries({
        queries: retailerIds.map((retailerId) => ({
            queryKey: codesKey(filters, retailerId),
            queryFn: ({ client }) => fetchLoadedPages(client, filters, retailerId),
            enabled: enabled && isPromotionsConfigured(),
            retry: shouldRetryPromotionsQuery,
            staleTime: 30_000,
        })),
    });
    const pending = retailerIds.map((retailerId, index) => {
        const pages = results[index].data?.pages ?? [];
        return { retailerId, nextToken: pages.at(-1)?.nextToken ?? undefined };
    });
    const appendPage = async (retailerId: string, nextToken: string) => {
        const page = await fetchPage(retailerId, nextToken);
        qc.setQueryData<CodePages>(codesKey(filters, retailerId), (old) =>
            old ? { pages: [...old.pages, page], pageParams: [...old.pageParams, nextToken] } : old,
        );
    };
    return {
        items: retailerIds.flatMap((retailerId, index) =>
            (results[index].data?.pages ?? []).flatMap((page) =>
                page.items.map((code) => ({ ...code, retailerId })),
            ),
        ),
        hasNextPage: pending.some((one) => one.nextToken !== undefined),
        isFetchingNextPage: fetchingMore,
        isSuccess:
            retailerIds.length === 0 ? !retailersLoading : results.every((one) => one.isSuccess),
        error: results.find((one) => one.error)?.error ?? null,
        loadMoreError,
        fetchNextPage: async () => {
            setFetchingMore(true);
            setLoadMoreFailure(null);
            const settled = await Promise.allSettled(
                pending.flatMap(({ retailerId, nextToken }) =>
                    nextToken === undefined ? [] : [appendPage(retailerId, nextToken)],
                ),
            );
            const failed = settled.find((one) => one.status === 'rejected');
            if (failed) setLoadMoreFailure({ key: filtersKey, error: failed.reason });
            setFetchingMore(false);
        },
        refetch: () => Promise.all(results.map((one) => one.refetch())),
    };
}

export interface CreateDiscountCodeInput {
    request: CreateDiscountCodeRequest;
    /** La cadena del código; solo la manda el admin de tenant. */
    retailerId?: string;
}

export function useCreateDiscountCode() {
    const qc = useQueryClient();
    return useMutation<DiscountCode, unknown, CreateDiscountCodeInput>({
        mutationFn: ({ request, retailerId }) =>
            retailerId
                ? promotionsClient.createDiscountCode(request, { retailerId })
                : promotionsClient.createDiscountCode(request),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}

export function useVoidDiscountCode() {
    const qc = useQueryClient();
    return useMutation<DiscountCode, unknown, { codeId: string; retailerId?: string }>({
        mutationFn: ({ codeId, retailerId }) =>
            retailerId
                ? promotionsClient.changeDiscountCodeStatus(
                      codeId,
                      { transition: 'VOID' },
                      { retailerId },
                  )
                : promotionsClient.changeDiscountCodeStatus(codeId, { transition: 'VOID' }),
        // Con éxito o con 409 la lista quedó vieja.
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}
